import type { Mods } from '../sim/stats';
import { P } from '../world/player';
/* ============================================================
   應急改裝（可選池見底之後的無限強化，2026-08-06）
   ============================================================

   ## 這一層要解決的問題

   量測（十顆種子）：玩家一場升到 **31–42 級**，但**只有 20 次真的開了選單**。
   差的那 20 級是 `ui/levelup.ts` 的空池分支 —— 11 個槽位全部滿級之後
   沒有任何項目可選，於是那一級靜靜換成「緊急補給 結構上限 +6」，不開選單。
   而且**全部發生在第 8 分鐘之後**，正好是「後期沒有決策可做」那段抱怨的來源。

   升級曲線放寬解決不了它（會升到 50 級，多出來的一樣變補給）；
   擴大池子要新內容。這一層的作法是：**把那 20 次無聲的補給變成 20 次三選一**。

   一場的決策數因此從 20 → 約 40，落在 `01` 的 32–38 帶寬之上；
   而且它同時補回 2026-08-06 銀箱改四選二拿掉的戰力
   （`.claude/notes/craft-gate-2026-08-06.md`）。

   ## 為什麼是這五項

   **刻意全部是純數值、而且互相排擠。** 這一層的存在理由是「永遠有得選」，
   不是「再開一條 build 路線」—— 規則類的效果放進來會變成
   「熬到池子見底才拿得到的隱藏強化」，那會鼓勵玩家刻意不拿東西。
   五項各對一條既有的統計軸，玩家在這裡回答的是
   「我這一場缺的是輸出、頻率、覆蓋、機動還是耐久」。

   ## 幅度：每級小、次數多

   一場約 20 次，全押同一項＝ ×2.2 上下（例如傷害 1.04^20 = 2.19）。
   對照組是它取代的東西（結構上限 +6 × 20 = +120 HP，幾乎沒有感覺）。
   實務上三選一不會每次都出現想要的那項，所以會攤成 2–3 項各 5–8 級。

   **沒有上限**（`max` 不存在）：有上限的話池子還是會見底，
   只是晚 20 級發生 —— 那就白做了。 */

export type Improvise = {
  id: string;
  n: string;
  kind: string;
  axes: string[];
  /** 卡片圖示的 key（沿用軸圖示，不另外畫）。 */
  ico: string;
  d: (lv: number) => string;
  mods: (lv: number) => Mods;
  /** 不在 `P.s` 裡的東西（結構上限）走這條增量路徑，照 `p-frame` 的慣例。 */
  onTake?: () => void;
};

function pct(mul: number, lv: number) { return Math.round((Math.pow(mul, lv) - 1) * 100); }
function cut(mul: number, lv: number) { return Math.round((1 - Math.pow(mul, lv)) * 100); }

export var IMPROVISES: Improvise[] = [
  {
    id: 'ov-dmg', n: '過載注入', kind: '應急改裝', axes: ['傷害'], ico: 'ax-傷害',
    d: function (lv) { return '全武裝傷害 +' + pct(1.04, lv) + '%'; },
    mods: function (lv) {
      var m = Math.pow(1.04, lv);
      return { mul: { atkDmg: m, midDmg: m, ultDmg: m } };
    }
  },
  {
    id: 'ov-rate', n: '節流解除', kind: '應急改裝', axes: ['冷卻'], ico: 'ax-冷卻',
    d: function (lv) { return '冷卻 −' + cut(0.97, lv) + '%、射速 +' + pct(1.035, lv) + '%'; },
    mods: function (lv) {
      return {
        mul: {
          midCdMax: Math.pow(0.97, lv), ultCdMax: Math.pow(0.97, lv),
          atkRate: Math.pow(1.035, lv)
        }
      };
    }
  },
  {
    id: 'ov-area', n: '擴散校正', kind: '應急改裝', axes: ['範圍'], ico: 'ax-範圍',
    d: function (lv) { return '所有爆炸與掃描範圍 +' + pct(1.04, lv) + '%'; },
    mods: function (lv) { return { mul: { area: Math.pow(1.04, lv) } }; }
  },
  {
    id: 'ov-move', n: '推進調校', kind: '應急改裝', axes: ['機動'], ico: 'ax-機動',
    d: function (lv) { return '移動速度 +' + pct(1.03, lv) + '%'; },
    mods: function (lv) { return { mul: { speed: Math.pow(1.03, lv) } }; }
  },
  {
    /* 唯一走 `onTake` 的一項：結構上限是這一層原本唯一給的東西
       （緊急補給 +6），留著它讓「我快死了」那一場仍然有純防禦的答案。
       +9 比原本的 +6 高一點，因為現在它要跟四個攻擊選項競爭一個名額。

       **`hpMax` 不在 `P.s` 裡**（它是 `P.hpMax`），所以修正層對它無效 ——
       照 `p-frame` 的慣例走 `onTake` 的增量路徑。寫成 `mods` 的話卡片會
       顯示「結構上限 +9」而血條完全不動，玩家會判定它是壞的。 */
    id: 'ov-hull', n: '裝甲增厚', kind: '應急改裝', axes: ['結構'], ico: 'ax-結構',
    d: function (lv) { return '結構上限 +' + (9 * lv); },
    mods: function () { return {}; },
    onTake: function () {
      P.hpMax += 9;
      P.hp = Math.min(P.hpMax, P.hp + 9);
    }
  }
];

export function improviseById(id: string) {
  for (var i = 0; i < IMPROVISES.length; i++) if (IMPROVISES[i].id === id) return IMPROVISES[i];
  return null;
}
